import React, { useEffect, useState } from 'react';
import { PlayerAccessCreateInput } from '../../services/playerAccessService';
import { organizationsService } from '../../services/organizationsService';
import { playersService } from '../../../../shared/services/players';
import SearchableDropdown from '../shared/SearchableDropdown';

interface AddPlayerAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PlayerAccessCreateInput) => Promise<void>;
}

interface DropdownOption { 
  id: string; 
  name: string; 
  subtitle?: string; 
}

const AddPlayerAccessModal: React.FC<AddPlayerAccessModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<PlayerAccessCreateInput>({
    player_id: '',
    organisation_id: '',
    access_type: '',
    granted_by: null,
    granted_at: new Date().toISOString(),
    active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [players, setPlayers] = useState<DropdownOption[]>([]);
  const [organizations, setOrganizations] = useState<DropdownOption[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [playersData, orgs] = await Promise.all([
          playersService.getPlayers(),
          organizationsService.getAll(),
        ]);
        setPlayers(playersData.map(p => {
          // Create subtitle with position and nationality/kit number for better recognition
          const subtitleParts = [];
          if (p.position) subtitleParts.push(p.position);
          if (p.nationality) subtitleParts.push(p.nationality);
          if (p.kit_number) subtitleParts.push(`#${p.kit_number}`);
          
          return { 
            id: p.id, 
            name: p.full_name,
            subtitle: subtitleParts.length > 0 ? subtitleParts.join(' • ') : undefined
          };
        }));
        setOrganizations(orgs.map(o => ({ id: o.id, name: `${o.name} (${o.subdomain})` })));
      } catch (e) {
        console.error('Error fetching dropdown data:', e);
      }
    };
    if (isOpen) fetchOptions();
  }, [isOpen]);

  const handleInputChange = (field: keyof PlayerAccessCreateInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.player_id) e.player_id = 'Player is required';
    if (!formData.organisation_id) e.organisation_id = 'Organization is required';
    if (!formData.access_type) e.access_type = 'Access type is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Player Access</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Player *</label>
            <SearchableDropdown options={players} value={formData.player_id} onChange={(v) => handleInputChange('player_id', v)} placeholder="Search players..." label=""/>
            {errors.player_id && <p className="text-red-500 text-xs mt-1">{errors.player_id}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization *</label>
            <SearchableDropdown options={organizations} value={formData.organisation_id} onChange={(v) => handleInputChange('organisation_id', v)} placeholder="Search organizations..." label=""/>
            {errors.organisation_id && <p className="text-red-500 text-xs mt-1">{errors.organisation_id}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access Type *</label>
            <select value={formData.access_type} onChange={(e) => handleInputChange('access_type', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.access_type ? 'border-red-300' : 'border-gray-300'}`}>
              <option value="">Select access type...</option>
              <option value="owner">Owner</option>
              <option value="participant">Participant</option>
              <option value="viewer">Viewer</option>
            </select>
            {errors.access_type && <p className="text-red-500 text-xs mt-1">{errors.access_type}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
            <div className="flex items-center space-x-3">
              <button type="button" onClick={() => handleInputChange('active', !formData.active)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.active ? 'bg-blue-600' : 'bg-gray-200'}`}>
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${formData.active ? 'translate-x-5' : 'translate-x-0'}`}/>
              </button>
              <span className="text-sm text-gray-600">{formData.active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">Cancel</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors">{isSaving ? 'Saving...' : 'Add Data'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPlayerAccessModal;



